import { useDeleteNode, useNode, usePatchNode } from "../../api/hooks.js";
import type { MediaNode } from "../../api/types.js";
import { Drawer } from "../../components/Drawer.js";
import {
  Badge,
  Button,
  CenteredSpinner,
  DriverBadge,
  ErrorState,
  HealthDot,
  Input,
  Label,
} from "../../components/ui.js";
import { ConfirmDialog } from "../../components/ConfirmDialog.js";
import { toast } from "../../components/toast.js";
import { useState } from "react";
import { Check, Copy, Trash2 } from "lucide-react";

function Chips({
  items,
  tone,
}: {
  items: string[];
  tone: "primary" | "slate";
}) {
  if (items.length === 0)
    return <span className="text-xs text-muted-foreground/50">—</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((c) => (
        <Badge key={c} tone={tone}>
          {c}
        </Badge>
      ))}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="text-sm text-foreground/90">{children}</div>
    </div>
  );
}

/** Cloudflare account credentials + webhook. API token is write-only (blank = keep current). */
function CfPanel({ node }: { node: MediaNode }) {
  const patch = usePatchNode(node.id);
  const cf = node.config.cf;
  const [accountId, setAccountId] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [copied, setCopied] = useState(false);

  async function copyUrl() {
    if (!cf?.webhookUrl) return;
    await navigator.clipboard.writeText(cf.webhookUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  async function save() {
    const cfPatch: { accountId?: string; apiToken?: string } = {};
    if (accountId.trim()) cfPatch.accountId = accountId.trim();
    if (apiToken.trim()) cfPatch.apiToken = apiToken.trim();
    if (Object.keys(cfPatch).length === 0) {
      toast("Nothing to update", "error");
      return;
    }
    try {
      // Saving creds (re-)subscribes the Stream webhook server-side if no secret is stored yet.
      await patch.mutateAsync({ config: { cf: cfPatch } });
      setAccountId("");
      setApiToken("");
      toast("Cloudflare credentials updated", "success");
    } catch {
      /* toasted globally */
    }
  }

  return (
    <div className="space-y-4 rounded-xl bg-card/50 p-4 ring-1 ring-inset ring-white/[0.06]">
      <div className="flex items-center justify-between">
        <Label>Cloudflare account</Label>
        <Badge tone={cf?.configured ? "primary" : "slate"}>
          {cf?.configured ? "configured" : "incomplete"}
        </Badge>
      </div>
      <Field label="Account ID">
        {cf?.accountId ?? <span className="text-muted-foreground">—</span>}
      </Field>
      {cf?.webhookUrl && (
        <div>
          <Label>Webhook URL</Label>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-lg bg-black/40 px-3 py-2 text-xs text-foreground/90 ring-1 ring-inset ring-white/10">
              {cf.webhookUrl}
            </code>
            <Button variant="secondary" onClick={copyUrl}>
              {copied ? (
                <Check className="h-4 w-4 text-emerald-400" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Auto-subscribed to this account's Stream webhook — no manual setup
            needed.
          </p>
        </div>
      )}
      <div className="space-y-2 border-t border-white/[0.06] pt-3">
        <Label>Update credentials</Label>
        <Input
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          autoComplete="off"
          placeholder="Account ID (blank = keep)"
        />
        <Input
          value={apiToken}
          onChange={(e) => setApiToken(e.target.value)}
          type="password"
          autoComplete="new-password"
          placeholder="API token (blank = keep)"
        />
        <div className="flex justify-end">
          <Button variant="primary" onClick={save} loading={patch.isPending}>
            Save credentials
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          API token is write-only — never shown. Saving (re-)subscribes the
          Stream webhook automatically; the signing secret is generated by
          Cloudflare and stored for you.
        </p>
      </div>
    </div>
  );
}

export function NodeDetailDrawer({
  nodeId,
  onClose,
}: {
  nodeId: string;
  onClose: () => void;
}) {
  const node = useNode(nodeId);
  const patch = usePatchNode(nodeId);
  const remove = useDeleteNode();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const n: MediaNode | undefined = node.data;

  async function toggleEnabled() {
    if (!n) return;
    try {
      await patch.mutateAsync({ enabled: !n.enabled });
      toast(n.enabled ? "Node disabled" : "Node enabled", "success");
    } catch {
      /* toasted globally */
    }
  }

  async function doDelete() {
    try {
      await remove.mutateAsync(nodeId);
      toast("Node deleted", "success");
      onClose();
    } catch {
      setConfirmDelete(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        n ? (
          <span className="flex items-center gap-2">
            {n.slug}
            <DriverBadge driver={n.driver} />
          </span>
        ) : (
          "Node"
        )
      }
      footer={
        n && (
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4" /> Delete node
            </Button>
            <Button
              variant={n.enabled ? "secondary" : "primary"}
              onClick={toggleEnabled}
              loading={patch.isPending}
            >
              {n.enabled ? "Disable" : "Enable"}
            </Button>
          </div>
        )
      }
    >
      {node.isLoading && <CenteredSpinner />}
      {node.error && <ErrorState message={node.error.message} />}
      {n && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name">{n.name}</Field>
            <Field label="Region">{n.region}</Field>
            <Field label="Health">
              <HealthDot health={n.health} />
            </Field>
            <Field label="Enabled">
              <span
                className={n.enabled ? "text-emerald-400" : "text-muted-foreground"}
              >
                {n.enabled ? "Yes" : "No (no new publishes)"}
              </span>
            </Field>
            <Field label="Last seen">
              {n.lastSeenAt
                ? new Date(n.lastSeenAt).toLocaleString()
                : "never"}
            </Field>
            <Field label="Active streams">{n.activeStreamCount}</Field>
            <Field label="Public host">
              {n.publicHost ?? (
                <span className="text-muted-foreground">— (managed)</span>
              )}
            </Field>
          </div>

          <div className="space-y-3 rounded-xl bg-card/50 p-4 ring-1 ring-inset ring-white/[0.06]">
            <Field label="Ingest">
              <Chips items={n.capabilities.ingest} tone="primary" />
            </Field>
            <Field label="Playback">
              <Chips items={n.capabilities.playback} tone="slate" />
            </Field>
            <Field label="Recording">
              <span
                className={
                  n.capabilities.recording
                    ? "text-emerald-400"
                    : "text-muted-foreground"
                }
              >
                {n.capabilities.recording ? "Supported" : "Not supported"}
              </span>
            </Field>
          </div>

          {n.driver === "cloudflare" && <CfPanel node={n} />}

          <div>
            <Label>Driver config</Label>
            <pre className="scrollbar-thin max-h-64 overflow-auto rounded-xl bg-black/40 p-3 text-xs leading-relaxed text-foreground/90 ring-1 ring-inset ring-white/[0.06]">
              {JSON.stringify(n.config, null, 2)}
            </pre>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Read-only view. Secrets (SRT passphrase, Cloudflare API token /
              webhook secret) are redacted and never shown here.
            </p>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete node"
        destructive
        confirmLabel="Delete"
        loading={remove.isPending}
        body="Removing this node stops all publish routing to it. Active streams should be force-ended first. Continue?"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </Drawer>
  );
}
